import * as Phaser from 'phaser';
import { Observer, GLOBAL_EVENT } from '../../Observer';
import b64toBlob from '../../../../utils/b64toBlob';
import { symmetricDifference, forEach } from 'ramda';
import store from '../../../../Redux/Store/store';
import { ActivateSideContextMenuAction } from '../../../../Redux/Actions/Atlas/contextSideMenu.action';
import { SelectImage } from '../../../../Redux/Actions/Atlas/images.action';

interface extrusion_object_interface {
    key: string;
    pixels: Array<Phaser.GameObjects.Rectangle>;
}

class RenderScene extends Phaser.Scene {
    // Grupo de imagenes que se van creando con this.add.image
    images_group: { key: string; image: any }[] = [];

    // Grupo de extrusión
    extrusion_objects: Array<extrusion_object_interface> = [];

    // Grupos de imagenes para comprobar si se agregan o no y si se eliminan
    temp_images_key: string[] = [];
    new_images: string[] = [];

    // Imagen seleccionada
    graphics: any;

    constructor() {
        super({
            key: 'RenderScene',
        });

        Observer.removeListener(GLOBAL_EVENT.REQUEST_IMAGE_CANVAS);
        Observer.removeListener(GLOBAL_EVENT.ZOOM_CHANGED);
        Observer.removeListener(GLOBAL_EVENT.IMAGE_CHARGED);
        Observer.removeListener(GLOBAL_EVENT.IMAGE_CHARGED_COMPLETE);
        Observer.removeListener(GLOBAL_EVENT.REQUEST_IMAGE_CANVAS);
        Observer.removeListener(GLOBAL_EVENT.IMAGE_DES_SELECTED);
        Observer.removeListener(GLOBAL_EVENT.IMAGE_SELECTED);
    }
    init() {
        this.graphics = this.add
            .graphics({ lineStyle: { width: 2, color: 0xff0000 }, fillStyle: { color: 0xaa0000 } })
            .setDepth(200);

        this.tweens.add({
            targets: this.graphics,
            // ease: (e: number) => (Math.floor(e) === 0),
            alpha: 0,
            ease: (e: number) => Math.round(e),
            yoyo: true,
            repeat: -1,
            duration: 1000,
        });

        // Obtenemos los datos de la imagen del CANVAS
        Observer.on(GLOBAL_EVENT.REQUEST_IMAGE_CANVAS, (options: any) => {
            this.scale.resize(this.scale.width * options.scale, this.scale.height * options.scale);
            this.scale.refresh();
            this.scale.updateScale();

            // Obtenemos la imagen escalada
            this.images_group.forEach((image_group: { key: string; image: any }) => {
                image_group.image.setScale(options.scale);
                image_group.image.x *= options.scale;
                image_group.image.y *= options.scale;
            });
            this.extrusion_objects.forEach((extrusion_object: extrusion_object_interface) => {
                extrusion_object.pixels.forEach((pixel: Phaser.GameObjects.Rectangle) => {
                    pixel.setScale(options.scale);
                    pixel.x *= options.scale;
                    pixel.y *= options.scale;
                });
            });

            this.game.renderer.snapshot((image: any) => {
                const src = image.src;
                this.scale.resize(
                    this.scale.width / options.scale,
                    this.scale.height / options.scale
                );
                this.scale.refresh();
                this.scale.updateScale();

                // Reiniciamos el escalado
                this.images_group.forEach((image_group: { key: string; image: any }) => {
                    image_group.image.setScale(1);
                    image_group.image.x /= options.scale;
                    image_group.image.y /= options.scale;
                });
                this.extrusion_objects.forEach((extrusion_object: extrusion_object_interface) => {
                    extrusion_object.pixels.forEach((pixel: Phaser.GameObjects.Rectangle) => {
                        pixel.setScale(1);
                        pixel.x /= options.scale;
                        pixel.y /= options.scale;
                    });
                });

                this.game.renderer.snapshot((image: any) => {
                    // Todo está listo y se emite el evento IMAGE_CANVAS_EXPORTED
                    const src_original = image.src;
                    const data = { ...options, src_original, src };
                    Observer.emit(GLOBAL_EVENT.IMAGE_CANVAS_EXPORTED, data);
                });
            });
        });

        // El Zoom visual
        Observer.on(GLOBAL_EVENT.ZOOM_CHANGED, (zoom_size) => {
            this.scale.setZoom(zoom_size);
            this.scale.refresh();
            this.scale.updateScale();
        });
    }

    preload(): void {
        // #------------- Cargamos imágenes
        Observer.on(GLOBAL_EVENT.IMAGE_CHARGED, (images) => {
            this.temp_images_key = [];
            this.new_images = [];
            this.textures.each((textures) => {
                const textures_key = textures.key;
                if (
                    textures_key !== '__DEFAULT' &&
                    textures_key !== '__MISSING' &&
                    !textures_key.startsWith('map')
                ) {
                    this.temp_images_key.push(textures_key);
                }
            }, this);

            this.new_images = images.map((image: any) => {
                this.load.image(image.id, URL.createObjectURL(b64toBlob(image.src)));
                return image.id;
            });

            this.load.start();
        });
    }

    create() {
        Observer.on(GLOBAL_EVENT.IMAGE_CHARGED_COMPLETE, (MaxRectGrid: any) => {
            const different = symmetricDifference(this.new_images, this.temp_images_key);
            if (this.new_images.length < this.temp_images_key.length) {
                this.removeTextures(different[0]);
            } else {
                // Acá agregamos solo las imagenes nuevas
                different.forEach((new_image: string) => {
                    const image = this.add
                        .image(0, 0, new_image)
                        .setOrigin(0, 0)
                        .setName(new_image)
                        .setInteractive({ cursor: 'pointer' });

                    image.on(
                        Phaser.Input.Events.POINTER_DOWN,
                        (event_mouse: any) => {
                            if (event_mouse.buttons === 1) {
                                this.select_a_image(image.name);
                            } else {
                                this.activate_context_menu(event_mouse, image);
                            }
                        },
                        this
                    );

                    this.images_group.push({
                        key: new_image,
                        image,
                    });
                });
            }
            if (MaxRectGrid !== undefined) {
                this.scale.setGameSize(MaxRectGrid.width, MaxRectGrid.height);
                this.scale.resize(MaxRectGrid.width, MaxRectGrid.height);
                this.scale.refresh();
                this.scale.updateScale();

                MaxRectGrid.rects.forEach((rect: any) => {
                    const image = this.images_group.find((image) => image.key === rect.data.id);
                    if (image !== undefined) {
                        image.image.x = rect.x;
                        image.image.y = rect.y;
                    }
                });
            }
            this.createExtrusion();
        });

        Observer.on(GLOBAL_EVENT.IMAGE_DES_SELECTED, () => {
            this.reset_images_selected();
        });
        Observer.on(GLOBAL_EVENT.IMAGE_SELECTED, (id) => {
            this.select_a_image(id);
        });
    }

    activate_context_menu(event_mouse: any, image: any) {
        if (event_mouse.button === 2) {
            const x = event_mouse.event.clientX;
            const y = event_mouse.event.clientY;
            store.dispatch(
                ActivateSideContextMenuAction({
                    x,
                    y,
                    id: image.name,
                    active: true,
                })
            );
            this.select_a_image(image.name);
        }
    }

    select_a_image(id: string) {
        const image = this.images_group.find((image: any) => image.key === id)?.image;
        setTimeout(() => {
            this.create_image_selected({
                x: image.x,
                y: image.y,
                width: image.width,
                height: image.height,
            });
            // TODO: Debido a que se deshabilita la seleccion de imagen debido al click estarior he puesto este tiempo
            // Debería analizarse para futuras versiones
            store.dispatch(SelectImage({ id: image.name }));
        }, 100);
    }

    create_image_selected({ x = 10, y = 10, width = 100, height = 100 }) {
        this.reset_images_selected();
        const rect = new Phaser.Geom.Rectangle(x, y, width, height);
        this.graphics.strokeRectShape(rect);
    }

    reset_images_selected() {
        this.graphics.clear();
    }

    createExtrusion() {
        this.removeExtrusion();
        const extrusion_size = store.getState().algorithmPanelAtlas.extrusion;

        if (extrusion_size > 0 && this.images_group.length > 0) {
            this.images_group.forEach((image) => {
                const texture = this.textures.get(image.key);
                const src = texture.getSourceImage();
                const canvas = this.textures
                    .createCanvas(`map_${image.key}`, src.width, src.height)
                    .draw(0, 0, src as HTMLImageElement);
                const pixel = new Phaser.Display.Color();
                // Extrusión
                const extrusion_objects_temp: extrusion_object_interface = {
                    key: image.key,
                    pixels: [],
                };

                for (let y = 0; y < src.height; y++) {
                    for (let x = 0; x < src.width; x++) {
                        // Arriba y abajo
                        canvas.getPixel(x, y, pixel);
                        if (
                            (x === 0 || y === 0 || x === src.width - 1 || y === src.height - 1) &&
                            !!pixel.alpha
                        ) {
                            const positionX =
                                x === 0
                                    ? x + image.image.x - extrusion_size
                                    : x + image.image.x + 1;
                            const positionY =
                                y === 0
                                    ? y + image.image.y - extrusion_size
                                    : y + image.image.y + 1;

                            // Izquierda y derecha
                            if (x === 0 || x === src.width - 1) {
                                const pixel_extruded = this.add
                                    .rectangle(
                                        positionX,
                                        y + image.image.y,
                                        extrusion_size,
                                        1,
                                        pixel.color
                                    )
                                    .setOrigin(0);
                                extrusion_objects_temp.pixels.push(pixel_extruded);
                            }

                            // Arriba y abajo
                            if (y === src.height - 1 || y === 0) {
                                const pixel_extruded = this.add
                                    .rectangle(
                                        x + image.image.x,
                                        positionY,
                                        1,
                                        extrusion_size,
                                        pixel.color
                                    )
                                    .setOrigin(0);
                                extrusion_objects_temp.pixels.push(pixel_extruded);
                            }
                            // Esquinas
                            if (
                                (x === 0 && y === 0) ||
                                (x === src.width - 1 && y === src.height - 1) ||
                                (x === src.width - 1 && y === 0) ||
                                (x === 0 && y === src.height - 1)
                            ) {
                                const pixel_extruded = this.add
                                    .rectangle(
                                        positionX,
                                        positionY,
                                        extrusion_size,
                                        extrusion_size,
                                        pixel.color
                                    )
                                    .setOrigin(0);
                                extrusion_objects_temp.pixels.push(pixel_extruded);
                            }
                        }
                    }
                }
                this.extrusion_objects.push(extrusion_objects_temp);
            });
        }
    }

    removeExtrusion() {
        forEach((extrusion_object) => {
            forEach((pixel) => {
                pixel.destroy();
            }, extrusion_object.pixels);
            if (this.textures.get(`map_${extrusion_object.key}`).key !== '__MISSING') {
                this.textures.remove(`map_${extrusion_object.key}`);
            }
        }, this.extrusion_objects);
        this.extrusion_objects = [];
    }

    removeTextures(textureName: string) {
        // Buscamos la imágen que se va a remover
        const image_to_destroy = this.images_group.find((image: any) => image.key === textureName);

        if (image_to_destroy !== undefined) {
            image_to_destroy.image.destroy(textureName);
        }

        this.textures.remove(textureName);
        // Eliminamos la textura que se ha removido
        this.images_group = this.images_group.filter((image: any) => image.key !== textureName);
    }
}

export default RenderScene;
